import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription,
  DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, ShieldCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

// Google 'G' icon — same SVG used in AuthPage
const GoogleIcon = () => (
  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

// Initiates Google OAuth from the modal, saving current URL so AuthCallback can restore it.
function handleGoogleReauth() {
  try {
    // Save the URL the user was on so AuthCallback can navigate back after re-auth.
    localStorage.setItem('sessionRestoreUrl', window.location.pathname + window.location.search);
  } catch (_) { /* ignore */ }
  const redirectAfter = window.location.origin + '/auth';
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  window.location.href = `${apiBase}/api/auth/google/init?redirect_after=${encodeURIComponent(redirectAfter)}`;
}

/**
 * SessionExpiredModal
 *
 * Soft re-login UX. When the customer's session dies mid-flow (e.g. /book
 * Step 3 → Submit → 401), the auth interceptor and AuthContext both fire
 * a `customer-session-expired` CustomEvent instead of hard-redirecting to
 * /auth. This component listens for that event and renders an inline
 * login dialog. On successful login it dispatches
 * `customer-session-restored` — the interceptor was waiting on that event
 * and replays the original failing axios request transparently. The
 * customer never loses their place.
 *
 * If the customer dismisses the dialog (X, Esc, or "Sign out"), we
 * dispatch `customer-session-expired-dismissed` so any pending promises
 * the interceptor was holding can reject cleanly.
 *
 * Why event-driven instead of a context method:
 *   - The interceptor is plain axios middleware outside React (installed
 *     at app bootstrap before <AuthProvider /> exists). Events are the
 *     simplest neutral bridge.
 *
 * Mounted once in App.js at the AuthProvider level. Only one instance
 * should exist app-wide; multiple opens stack as repeated event fires.
 */
export default function SessionExpiredModal() {
  const navigate = useNavigate();
  const { login: contextLogin } = useAuth();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  // Tracks whether we've already restored the session — guards against
  // a double-fire of `customer-session-restored` if multiple 401s queue.
  const restoredRef = useRef(false);

  useEffect(() => {
    const onExpired = () => {
      restoredRef.current = false;
      // Try to prefill from the "remember email" feature that already
      // exists on the login page.
      try {
        const remembered = localStorage.getItem('rememberedEmail');
        if (remembered) setEmail(remembered);
      } catch (_) { /* ignore */ }
      setOpen(true);
    };
    window.addEventListener('customer-session-expired', onExpired);
    return () => window.removeEventListener('customer-session-expired', onExpired);
  }, []);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setErrorMsg('');
    if (!email || !password) {
      setErrorMsg('Please enter your email and password.');
      return;
    }
    setSubmitting(true);
    try {
      // Use the existing AuthContext login so `lastLoginAt` is stamped
      // and all the same side-effects (token storage, user state, "Session
      // extended" cooldown reset) happen exactly as if the user logged in
      // via /auth.
      await contextLogin(email, password);
      restoredRef.current = true;
      setOpen(false);
      setPassword('');
      setErrorMsg('');
      toast.success('Welcome back — picking up where you left off.', { duration: 2500 });
      window.dispatchEvent(new CustomEvent('customer-session-restored'));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // FastAPI sometimes returns validation arrays — coerce to string.
      const msg = typeof detail === 'string'
        ? detail
        : (Array.isArray(detail) && detail[0]?.msg) || err?.message || 'Login failed';
      setErrorMsg(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignOut = () => {
    // Customer chose to bail out instead of re-logging in. Reject any
    // queued request promises in the interceptor and bounce to /auth so
    // they can decide what to do next.
    if (!restoredRef.current) {
      window.dispatchEvent(new CustomEvent('customer-session-expired-dismissed'));
    }
    setOpen(false);
    setPassword('');
    setErrorMsg('');
    navigate('/auth');
  };

  // Don't allow ESC / outside click to silently close — would leave
  // pending promises hanging forever. Force them through one of the
  // two explicit actions (Continue or Sign out).
  const handleOpenChange = (newOpen) => {
    if (newOpen) return setOpen(true);
    if (submitting) return;
    handleSignOut();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        // Disable dismissal on outside click & escape; we want explicit
        // intent because the user has pending work that depends on this.
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="session-expired-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-blue-600" />
            Your session expired
          </DialogTitle>
          <DialogDescription>
            Log in again to keep going — your booking selections are saved.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-3 pt-2">
          <div className="space-y-1">
            <Label htmlFor="se-email">Email</Label>
            <Input
              id="se-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              autoFocus
              data-testid="session-expired-email-input"
              required
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="se-password">Password</Label>
            <Input
              id="se-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              data-testid="session-expired-password-input"
              required
            />
          </div>

          {errorMsg && (
            <p className="text-xs text-red-600" data-testid="session-expired-error">
              {errorMsg}
            </p>
          )}

          <DialogFooter className="gap-2 pt-2 flex-col sm:flex-col">
            <div className="flex gap-2 w-full">
              <Button
                type="button"
                variant="ghost"
                onClick={handleSignOut}
                disabled={submitting}
                data-testid="session-expired-signout-btn"
                className="flex-1"
              >
                Sign out
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="session-expired-submit-btn"
                className="flex-1"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Logging in…</>
                ) : (
                  'Continue'
                )}
              </Button>
            </div>

            <div className="relative w-full">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">or</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => {
                // Clean up any pending interceptor promises before leaving the page.
                if (!restoredRef.current) {
                  window.dispatchEvent(new CustomEvent('customer-session-expired-dismissed'));
                }
                handleGoogleReauth();
              }}
              data-testid="session-expired-google-btn"
            >
              <GoogleIcon />
              Continue with Google
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
